import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Input,
  Tabs,
  Tab,
  Card,
  CardBody,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  SortDescriptor
} from "@nextui-org/react";
import { useTranslation } from "react-i18next";
import { SearchIcon, ChevronDownIcon } from "@/components/icons";
import DefaultLayout from "@/layouts/default";
import { ordersAPI } from "@/services/api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./datepicker-custom.css";
import { useNavigate } from "react-router-dom";
import { generateOrderHistoryPDF } from "@/utils/pdf-generator";
import useLanguageDirection from "@/utils/useLanguageDirection";
import DataTable, { Column, StatusMapping } from "@/components/DataTable";
import { formatDate } from "@/utils/dateUtils";

interface OrderItem {
  id: number;
  book_id: number;
  book_name: string;
  status: string;
  quantity: number;
  original_quantity?: number;
  supplier_name?: string;
  phone_number?: string;
  price_per_book?: number;
  total_purchase?: number;
  client_id: number;
  created_at?: string;
  updated_at?: string;
  order_date?: string;
}

interface OrderMetrics {
  totalOrders: number;
  totalItems: number;
  totalInvested: number;
}

const OrderHistoryPage: React.FC = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguageDirection();
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<OrderItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState<string>("total");
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "ascending" | "descending";
  }>({ key: "updated_at", direction: "descending" });
  const itemsPerPage = 10;
  const tableRef = useRef<HTMLDivElement>(null);
  const [isPdfLoading, setIsPdfLoading] = useState(false);

  // Metrics for different time periods
  const [timeframeMetrics, setTimeframeMetrics] = useState<Record<string, OrderMetrics>>({
    total: { totalOrders: 0, totalItems: 0, totalInvested: 0 },
    monthly: { totalOrders: 0, totalItems: 0, totalInvested: 0 },
    daily: { totalOrders: 0, totalItems: 0, totalInvested: 0 },
  });

  const navigate = useNavigate();

  // Format currency to display prices
  const formatPrice = (value: number | string | undefined): string => {
    if (value === undefined) return "0";
    const numericValue = typeof value === "string" ? parseFloat(value) : value;
    return !isNaN(numericValue) ? numericValue.toLocaleString() : "0";
  };

  // Fetch all orders from the API
  useEffect(() => {
    const fetchOrders = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await ordersAPI.getAll();
        
        // Transform API response to match our interface
        const transformedOrders = response.map((order: any) => ({
          id: order.id,
          book_id: order.book_id,
          book_name: order.book_name || order.bookName,
          status: order.status,
          quantity: order.quantity,
          original_quantity: order.original_quantity,
          supplier_name: order.supplier_name,
          phone_number: order.phone_number,
          price_per_book: order.price_per_book,
          total_purchase: order.total_purchase,
          client_id: order.client_id,
          created_at: order.created_at,
          updated_at: order.updated_at,
          order_date: order.order_date,
        }));
        
        setOrders(transformedOrders);
        setFilteredOrders(transformedOrders);
        
        // Calculate metrics by timeframe
        calculateMetricsByTimeframe(transformedOrders);
      } catch (error) {
        console.error("Error fetching orders:", error);
        setError("Failed to fetch order history. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, []);

  // Calculate metrics by different time periods
  const calculateMetricsByTimeframe = (orderList: OrderItem[]) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day
    
    // Daily metrics (today only)
    const dailyOrders = orderList.filter((order) => {
      const orderDate = new Date(order.updated_at || order.created_at || "");
      orderDate.setHours(0, 0, 0, 0);
      return orderDate.getTime() === today.getTime();
    });

    // Monthly metrics (current month)
    const monthlyOrders = orderList.filter((order) => {
      const orderDate = new Date(order.updated_at || order.created_at || "");
      return orderDate.getMonth() === today.getMonth() && 
             orderDate.getFullYear() === today.getFullYear();
    });

    // Calculate metrics for each timeframe
    const calculateMetrics = (orders: OrderItem[]): OrderMetrics => {
      // Get completed orders for investment calculation
      const completedOrders = orders.filter(order => order.status === "completed");
      
      return {
        totalOrders: orders.length,
        totalItems: completedOrders.reduce((sum, order) => sum + order.quantity, 0),
        totalInvested: completedOrders.reduce((sum, order) => sum + (order.total_purchase || 0), 0),
      };
    };

    setTimeframeMetrics({
      daily: calculateMetrics(dailyOrders),
      monthly: calculateMetrics(monthlyOrders),
      total: calculateMetrics(orderList),
    });
  };

  // Handle search functionality
  const handleSearch = (value: string) => {
    setSearchQuery(value);
    filterOrders(value, selectedStatus, startDate, endDate);
  };

  // Handle status filter change
  const handleStatusChange = (status: string) => {
    setSelectedStatus(status);
    filterOrders(searchQuery, status, startDate, endDate);
  };

  // Filter orders based on search query, status, and date range
  const filterOrders = (
    query: string = searchQuery,
    status: string = selectedStatus,
    start: Date | null = startDate,
    end: Date | null = endDate
  ) => {
    let filtered = [...orders];

    // Filter by status
    if (status !== "all") {
      filtered = filtered.filter(order => order.status === status);
    }

    // Filter by date range
    if (start && end) {
      filtered = filtered.filter(order => {
        const orderDate = order.updated_at || order.created_at;
        if (!orderDate) return false;
        
        const date = new Date(orderDate);
        return date >= start && date <= new Date(end.setHours(23, 59, 59, 999));
      });
    }

    // Filter by search query
    if (query.trim() !== "") {
      const lowercaseQuery = query.toLowerCase();
      filtered = filtered.filter(order => 
        order.book_name.toLowerCase().includes(lowercaseQuery) ||
        (order.supplier_name && order.supplier_name.toLowerCase().includes(lowercaseQuery)) ||
        String(order.quantity).includes(lowercaseQuery) ||
        (order.price_per_book && String(order.price_per_book).includes(lowercaseQuery))
      );
    }

    // Apply sorting
    filtered = sortOrders(filtered);
    
    setFilteredOrders(filtered);
    
    // Reset sort configuration to ensure consistent serial numbering
    setSortConfig({
      key: "updated_at",
      direction: "descending"
    });
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSearchQuery("");
    setSelectedStatus("all");
    setStartDate(null);
    setEndDate(null);
    setFilteredOrders(orders);
  };

  // Sort orders based on current sort configuration
  const sortOrders = (ordersToSort: OrderItem[]) => {
    return [...ordersToSort].sort((a, b) => {
      const aValue = a[sortConfig.key as keyof OrderItem];
      const bValue = b[sortConfig.key as keyof OrderItem];
      
      if (aValue === undefined || bValue === undefined) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      
      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortConfig.direction === "ascending" 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "ascending" 
          ? aValue - bValue 
          : bValue - aValue;
      }
      
      return 0;
    });
  };

  // Get the current timeframe's metrics
  const metrics = timeframeMetrics[selectedTimeframe] || timeframeMetrics.total;

  // Generate PDF from the filtered data
  const handleExportPDF = async () => {
    if (!startDate || !endDate) {
      return;
    }
    
    try {
      setIsPdfLoading(true);
      
      // Filter orders by the date range
      const dateFilteredOrders = orders.filter(order => {
        const orderDate = new Date(order.updated_at || order.created_at || "");
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        return orderDate >= start && orderDate <= end;
      });
      
      // Use the utility function to generate the PDF with translation
      await generateOrderHistoryPDF(
        dateFilteredOrders,
        metrics, // Using the currently selected timeframe metrics
        {
          title: "Bookstore Order Statement",
          dateRange: {
            start: startDate,
            end: endDate
          },
          companyName: "Naqeer BookStore Management",
          isRTL: isRTL,
          translate: t // Pass the translation function
        }
      );
      
    } catch (error) {
      console.error("Error generating PDF:", error);
      setError("Failed to generate PDF. Please try again.");
    } finally {
      setIsPdfLoading(false);
    }
  };

  // Status mapping for the DataTable component
  const statusMapping: StatusMapping = {
    "completed": {
      color: "success",
      label: "completed"
    },
    "canceled": {
      color: "danger",
      label: "canceled"
    },
    "pending": {
      color: "warning",
      label: "pending"
    },
    "added": {
      color: "primary",
      label: "added"
    }
  };

  // Table columns configuration
  const columns: Column<OrderItem>[] = [
    {
      key: "book_name",
      label: "Book Name",
      sortable: true
    },
    {
      key: "status",
      label: "Status",
      sortable: true
    },
    {
      key: "quantity",
      label: "Quantity",
      sortable: true,
      renderCell: (item) => (
        <>
          {item.quantity}
          {item.original_quantity && item.original_quantity !== item.quantity && (
            <span className="text-gray-500 text-xs ml-1">
              (Ordered: {item.original_quantity})
            </span>
          )}
        </>
      )
    },
    {
      key: "supplier_name",
      label: "Supplier",
      sortable: true
    },
    {
      key: "price_per_book",
      label: "Price/Book",
      sortable: true,
      renderCell: (item) => formatPrice(item.price_per_book || 0)
    },
    {
      key: "total_purchase",
      label: "Total Cost",
      sortable: true,
      renderCell: (item) => formatPrice(item.total_purchase || 0)
    },
    {
      key: "updated_at",
      label: "Date",
      sortable: true,
      renderCell: (item) => formatDate(item.updated_at || item.created_at)
    }
  ];

  // Handler for sorting
  const handleSortChange = (descriptor: SortDescriptor) => {
    setSortConfig({
      key: descriptor.column as string,
      direction: descriptor.direction as "ascending" | "descending"
    });
    
    const sorted = sortOrders([...filteredOrders]);
    setFilteredOrders(sorted);
  };

  return (
    <DefaultLayout>
      <div className="flex justify-between items-center mb-6 px-8">
        <h1 className="text-3xl font-bold">{t("Order History")}</h1>
        <Button 
          color="primary" 
          variant="flat"
          onClick={() => navigate("/orders")}
        >
          {t("Back to Orders")}
        </Button>
      </div>
      
      {/* Time Period Tabs */}
      <div className="px-8 mb-6">
        <Tabs
          aria-label="Time period options"
          className="mb-4"
          selectedKey={selectedTimeframe}
          onSelectionChange={(key) => setSelectedTimeframe(key as string)}
        >
          <Tab key="daily" title={t("Today")} />
          <Tab key="monthly" title={t("This Month")} />
          <Tab key="total" title={t("All Time")} />
        </Tabs>

        {/* Metrics Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardBody className="text-center">
              <p className="text-md text-default-500">{t("Total Orders")}</p>
              <h3 className="text-2xl font-bold">{metrics.totalOrders}</h3>
            </CardBody>
          </Card>
          
          <Card>
            <CardBody className="text-center">
              <p className="text-md text-default-500">{t("Total Invested")}</p>
              <h3 className="text-2xl font-bold">{formatPrice(metrics.totalInvested)}</h3>
            </CardBody>
          </Card>
          
          <Card>
            <CardBody className="text-center">
              <p className="text-md text-default-500">{t("Items Purchased")}</p>
              <h3 className="text-2xl font-bold">{metrics.totalItems}</h3>
            </CardBody>
          </Card>
        </div>
      </div>
      
      {/* Filter and Search Controls */}
      <div className="flex flex-col md:flex-row justify-between gap-4 mb-4 px-8">
        <div className="flex flex-wrap gap-3 items-center">
          <Dropdown>
            <DropdownTrigger>
              <Button 
                variant="flat" 
                endContent={<ChevronDownIcon />}
              >
                {t("Status")}: {selectedStatus === "all" ? t("All") : t(selectedStatus)}
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              aria-label="Status filter"
              onAction={(key) => handleStatusChange(key as string)}
              selectedKeys={[selectedStatus]}
              selectionMode="single"
            >
              <DropdownItem key="all">{t("All")}</DropdownItem>
              <DropdownItem key="completed">{t("Completed")}</DropdownItem>
              <DropdownItem key="canceled">{t("Canceled")}</DropdownItem>
              <DropdownItem key="pending">{t("Pending")}</DropdownItem>
              <DropdownItem key="added">{t("Added")}</DropdownItem>
            </DropdownMenu>
          </Dropdown>
          
          <div className="flex flex-row items-center gap-2">
            <div className="flex-none">
              <DatePicker
                selected={startDate}
                onChange={(date: Date | null) => {
                  setStartDate(date);
                  filterOrders(searchQuery, selectedStatus, date, endDate);
                }}
                placeholderText={t("StartDate")}
                maxDate={endDate || new Date()}
                dateFormat="dd/MM/yyyy"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                className="p-2 border border-gray-300 rounded-md"
              />
            </div>
            <div className="flex-none">
              <DatePicker
                selected={endDate}
                onChange={(date: Date | null) => {
                  setEndDate(date);
                  filterOrders(searchQuery, selectedStatus, startDate, date);
                }}
                placeholderText={t("EndDate")}
                minDate={startDate ? startDate : undefined}
                maxDate={new Date()}
                dateFormat="dd/MM/yyyy"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                className="p-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>
          
          <Button
            variant="flat"
            onClick={handleResetFilters}
          >
            {t("Reset Filters")}
          </Button>
          
          {/* PDF Export Button - Only visible when both dates are selected */}
          {startDate && endDate && (
            <Button
              color="primary"
              onClick={handleExportPDF}
              isLoading={isPdfLoading}
              startContent={
                <svg 
                  width="20" 
                  height="20" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M14 3v4a1 1 0 0 0 1 1h4" />
                  <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" />
                  <path d="M9 17h6" />
                  <path d="M9 13h6" />
                </svg>
              }
            >
              {t("Export PDF")}
            </Button>
          )}
        </div>
        
        <Input
          isClearable
          className="w-full md:max-w-[320px]"
          placeholder={t("Search by book, supplier...")}
          size="md"
          startContent={<SearchIcon className="text-default-400" />}
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          onClear={() => handleSearch("")}
        />
      </div>
      
      {/* Error Message */}
      {error && (
        <div className="p-4 mx-8 mb-4 bg-red-100 border border-red-400 text-red-700 rounded">
          <p>{error}</p>
        </div>
      )}
      
      {/* Order History Table using DataTable component */}
      <div ref={tableRef} className="px-8">
        <DataTable 
          columns={columns}
          data={filteredOrders}
          isLoading={isLoading}
          emptyContent={searchQuery || selectedStatus !== "all" || startDate || endDate
            ? t("No orders match your filters. Try adjusting your search criteria.")
            : t("No order history found.")}
          statusField="status"
          statusMapping={statusMapping}
          pageSize={itemsPerPage}
          sortDescriptor={{
            column: sortConfig.key,
            direction: sortConfig.direction
          }}
          onSortChange={handleSortChange}
          addSerialNumbers={true}
        />
      </div>
    </DefaultLayout>
  );
};

export default OrderHistoryPage; 